import React from "react";
import { useParams } from "react-router-dom";
import "./receipt.css";
import { axiosget } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import is_null from "../../helpers/is_null";
import is_money from "../../helpers/is_money";
import { useQuery } from "@tanstack/react-query";
import ErrorResponse from "../../components/ErrorResponse";
import useAuthStore from "../../stores/useAuthStore";
import { staffBackend } from "../../helpers/uri";

const PaymentReceipt = () => {
  const { id } = useParams();
  const { settings, officials } = useAuthStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ["myReceipt", id],
    queryFn: async () => {
      const req = await axiosget(`ms_transactions/${id}`);
      return req?.data;
    },
  });

  const fullAmount = !is_null(data)
    ? data?.find?.items?.reduce((acc, obj) => acc + obj.amount, 0)
    : 0;

  let allAmount = fullAmount;
  if (data?.scholar) {
    const percent = Number.parseFloat(data?.scholar?.percentage) / 100;
    allAmount = allAmount * percent;
  }

  const student = data?.find?.student;
  const transaction = data?.find;
  const gatewayName = transaction?.rrrresponse?.gatewayDisplayName ||
    transaction?.rrrresponse?.gateway ||
    "Remita";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="receipt-container">
      {isLoading && <Loader />}
      {error && <ErrorResponse type="danger" message={`Error: ${error?.message || error}`} />}
      {data?.type !== undefined && <ErrorResponse type="danger" message={data?.message} />}

      {data?.type === undefined && !is_null(data) && (
        <div className="receipt-wrapper">
          {/* Header */}
          <div className="receipt-header">
            <div className="header-logo">
              <img src={settings?.logo ? `${staffBackend}${settings.logo}` : "/images/college-logo.png"} alt="Institution Logo" />
            </div>
            <div className="header-info">
              <h1>{settings?.institutionName || "ADAMU TAFAWA BALEWA COLLEGE OF EDUCATION, KANGERE"}</h1>
              <p className="sub-title">DIRECTORATE OF STUDIES</p>
              <p className="affiliation">IN AFFILIATION TO USMAN DANFODIYO UNIVERSITY</p>
              <p className="address">MAIN CAMPUS, ALONG BAUCHI-GOMBE ROAD, KANGERE, BAUCHI STATE</p>
            </div>

          </div>

          {/* Title */}
          <div className="receipt-title">
            <h2>OFFICIAL PAYMENT RECEIPT</h2>
            <div className="receipt-number">Receipt #{transaction?._id?.slice(-8).toUpperCase()}</div>
          </div>

          {/* Status Badge */}
          <div className={`status-badge ${transaction?.status ? "paid" : "pending"}`}>
            {transaction?.status ? "✓ PAYMENT CONFIRMED" : "⏳ PENDING"}
          </div>

          {/* Student & Payment Info */}
          <div className="info-grid">
            <div className="info-section">
              <h3>Student Information</h3>
              <div className="info-row">
                <span className="label">Registration No:</span>
                <span className="value">{student?.studentid || "N/A"}</span>
              </div>
              <div className="info-row">
                <span className="label">Full Name:</span>
                <span className="value">{`${student?.firstname || ""} ${student?.middlename || ""} ${student?.lastname || ""}`.trim() || "N/A"}</span>
              </div>
              <div className="info-row">
                <span className="label">School:</span>
                <span className="value">{student?.programme?.department?.college?.name || "N/A"}</span>
              </div>
              <div className="info-row">
                <span className="label">Department:</span>
                <span className="value">{student?.programme?.department?.name || "N/A"}</span>
              </div>
              <div className="info-row">
                <span className="label">Programme:</span>
                <span className="value">{student?.programme?.name || "N/A"}</span>
              </div>
            </div>

            <div className="info-section">
              <h3>Payment Details</h3>
              <div className="info-row">
                <span className="label">Session:</span>
                <span className="value">{transaction?.session?.name || "N/A"}</span>
              </div>
              <div className="info-row">
                <span className="label">Payment Reference:</span>
                <span className="value reference">{transaction?.rrr || "N/A"}</span>
              </div>
              <div className="info-row">
                <span className="label">Payment Method:</span>
                <span className="value">{gatewayName}</span>
              </div>
              <div className="info-row">
                <span className="label">Amount Paid:</span>
                <span className="value amount">{is_money(allAmount)}</span>
              </div>
              <div className="info-row">
                <span className="label">Payment Date:</span>
                <span className="value">{formatDate(transaction?.verifiedAt || transaction?.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Fee Items Table */}
          <div className="items-section">
            <h3>Fee Breakdown</h3>
            <table className="items-table">
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {transaction?.items?.map((item, index) => (
                  <tr key={item._id || index}>
                    <td>{index + 1}</td>
                    <td>{item.label}</td>
                    <td className="amount-cell">{is_money(item.amount)}</td>
                  </tr>
                ))}
                {data?.scholar && (
                  <>
                    <tr className="subtotal-row">
                      <td></td>
                      <td>Subtotal</td>
                      <td className="amount-cell">{is_money(fullAmount)}</td>
                    </tr>
                    <tr className="discount-row">
                      <td></td>
                      <td>Scholarship Discount ({data?.scholar?.percentage}%)</td>
                      <td className="amount-cell discount">-{is_money(fullAmount - allAmount)}</td>
                    </tr>
                  </>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td></td>
                  <td><strong>Total Paid</strong></td>
                  <td className="total-cell">{is_money(allAmount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Declaration */}
          <div className="declaration">
            <p>
              This is to certify that the sum of <strong>{is_money(allAmount)}</strong> was received from{" "}
              <strong>{`${student?.firstname || ""} ${student?.lastname || ""}`.trim()}</strong> ({student?.studentid})
              in respect of {transaction?.session?.name} Academic Session fees payment via {gatewayName}.
            </p>
            <p className="warning">⚠️ FEES ARE NOT REFUNDABLE AFTER PAYMENT</p>
          </div>

          {/* Signatures */}
          <div className="signatures">
            <div className="signature-block">
              <div className="signature-line"></div>
              <p className="signature-title">Student's Signature</p>
              <p className="signature-name">{`${student?.firstname || ""} ${student?.lastname || ""}`.trim()}</p>
            </div>
            <div className="signature-block">
              <div className="signature-line"></div>
              <p className="signature-title">Bursar's Signature</p>
              <p className="signature-name">Head of Bursary Department</p>
            </div>
          </div>

          {/* Footer */}
          <div className="receipt-footer">
            <p>This receipt is computer-generated and valid without signature when verified online.</p>
            <p>For verification, visit: {window.location.href}</p>
          </div>

          {/* Print Button */}
          <button className="print-btn" onClick={() => window.print()}>
            🖨️ Print Receipt
          </button>
        </div>
      )}

      {data?.type === undefined && !isLoading && is_null(data) && (
        <div className="no-data">
          <p>No payment information found for this transaction.</p>
        </div>
      )}
    </div>
  );
};

export default PaymentReceipt;
